import React from 'react';

// CTV-689: recover deck.gl maps from WebGL context loss. Browsers cap live
// WebGL contexts per page/principal (Firefox is the tightest); when the cap is
// hit they silently kill the oldest context, leaving that canvas permanently
// blank. luma.gl's WebGLDevice.destroy() deliberately leaves its GL context to
// garbage collection, so tab-bouncing in the wizard orphans contexts until a
// live map's context gets reaped.
//
// Two-part fix:
//  - onDeviceInitialized chains device.lost (the promise luma resolves when the
//    native webglcontextlost event fires; DeckProps.onError is NOT called for
//    context loss in deck.gl 9.3) and remounts the Deck via a key bump, up to
//    maxRecoveries consecutive losses.
//  - unmount explicitly frees the context via WEBGL_lose_context so unmounted
//    maps stop counting toward the browser cap.

// Structural on purpose: the runtime WebGLDevice has `.gl` but the abstract
// luma Device type does not, and apps/selfservice doesn't depend on luma
// directly.
export interface DeviceLike {
  lost: Promise<unknown>;
  gl?: WebGLRenderingContext | WebGL2RenderingContext;
}

export interface WebglContextRecovery {
  /** Key for <DeckGL>; each bump mounts a fresh canvas/context. */
  deckKey: number;
  /** Pass to <DeckGL onDeviceInitialized>. */
  onDeviceInitialized: (device: DeviceLike) => void;
  /** True once maxRecoveries consecutive losses are burned; stop remounting. */
  exhausted: boolean;
  /** Clears exhausted and grants one more remount. */
  reset: () => void;
}

export function useWebglContextRecovery(maxRecoveries = 3): WebglContextRecovery {
  const [deckKey, setDeckKey] = React.useState(0);
  const [exhausted, setExhausted] = React.useState(false);
  const deviceRef = React.useRef<DeviceLike | null>(null);
  const disposedRef = React.useRef(false);
  const recoveryCountRef = React.useRef(0);

  const onDeviceInitialized = React.useCallback(
    (device: DeviceLike) => {
      deviceRef.current = device;
      device.lost.then(() => {
        // Stale-device guard: ignore losses from a device we already replaced
        // (StrictMode double-mounts) or triggered ourselves on unmount.
        if (disposedRef.current || deviceRef.current !== device) return;
        recoveryCountRef.current += 1;
        if (recoveryCountRef.current > maxRecoveries) {
          setExhausted(true);
        } else {
          setDeckKey((k) => k + 1);
        }
      });
    },
    [maxRecoveries],
  );

  React.useEffect(() => {
    disposedRef.current = false;
    return () => {
      // Set disposed BEFORE losing the context so the self-triggered `lost`
      // resolution doesn't schedule a remount of an unmounted component.
      disposedRef.current = true;
      try {
        deviceRef.current?.gl?.getExtension('WEBGL_lose_context')?.loseContext();
      } catch {
        /* freeing the context is best-effort */
      }
    };
  }, []);

  const reset = React.useCallback(() => {
    recoveryCountRef.current = 0;
    setExhausted(false);
    setDeckKey((k) => k + 1);
  }, []);

  return { deckKey, onDeviceInitialized, exhausted, reset };
}
